import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useCallback, useMemo } from 'react';
import { RW_INTELLIGENCE_FLAGS } from '../feature-flags/rw-intelligence-flags';
import { useFeatureFlags } from '../feature-flags/use-feature-flags';
import MilaService from '../../services/mila-service/mila-service';
import { PatientCase } from '../../services/mila-service/types';
import { LeadsDataRequest } from './data/types';

export type PatientCaseWithTimetoken = PatientCase & {
  timetoken: number;
};

const DEFAULT_PAGE_ITEM_COUNT = 25;

export interface LeadsManagementContext {
  getLeadsHistory: (request: LeadsDataRequest) => Promise<PatientCaseWithTimetoken[]>;
}

export const useLeadsManagement = (): LeadsManagementContext => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { hasFeature } = useFeatureFlags();
  const milaService = useMemo(() => MilaService.getInstance(config, oidcService), [config, oidcService]);

  const getLeadsHistory = useCallback(
    async (request: LeadsDataRequest) => {
      try {
        const start = request.start ? new Date(parseInt(request.start)) : undefined;
        const { mailboxes, patientId } = request.query;

        const locationId = mailboxes?.[0]?.locationId;

        if (!locationId || !patientId) return [];

        const hasRWIntelligence = RW_INTELLIGENCE_FLAGS.some((flag) => hasFeature(locationId, flag));

        if (!hasRWIntelligence) return [];

        const leads = await milaService.getPatientCases(locationId, patientId, {
          timeStart: start?.toISOString(),
          pageSize: DEFAULT_PAGE_ITEM_COUNT,
          order: 'DESC'
        });

        return leads
          .filter((l) => l.timeOfCompletion)
          .map(
            (lead: PatientCase) =>
              ({
                ...lead,
                timetoken: new Date(lead.timeOfCompletion!).getTime()
              } as PatientCaseWithTimetoken)
          );
      } catch (error) {
        console.error('Error fetching leads history:', error);

        return [];
      }
    },
    [milaService, hasFeature]
  );

  return {
    getLeadsHistory
  };
};
